import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { detect } from 'detect-browser';
import styled from 'styled-components';

import Donut from '../../components/Donut';
import Logo from '../../components/Svg/ActionButtonIcon';

import { onGoToNextCard } from '../Widget/actions';

import { CardTypes } from '../../utils/enums';
import { isLocalRepresentativeAction } from '../../utils/localRepresentative/localRepresentativeActionCheck';
import {
    relevantRepresentatives,
    joinEnglish
} from '../../utils/localRepresentative/parser';
import { Placeholders } from '../../utils/fillDynamicText';
import { isPortrait } from '../../utils/size';
import { copyFor, capitalization } from '../../copy/copy';
import countdownAnimation from '../../components/Widget/CountdownAnimation.json';
import rightArrow from '../../assets/images/right-arrow.png';

let lottie = {};
import('lottie-web').then(l => (lottie = l));

const Container = styled.div`
    position: absolute;
    left: 0;
    width: ${props => (props.isVisual && !isPortrait() ? 70 : 100)}%;
    cursor: pointer;
    bottom: ${props => (props.hasNextAction && !props.hideFooter ? '40px' : '0')};

    &.hide {
        display: none;
    }
`;

const Body = styled.div`
    height: 53px;
    background-color: #ffffff;
    box-shadow: 0 12px 24px 0 rgba(0, 0, 0, 0.2);
    border: solid 1px #f3f1f1;
    border-bottom: 0;
    margin: 0 1em;
    padding: 0.75em 1em;
    display: flex;
    flex-direction: row;

    @media (max-width: 30em) {
        height: auto;
    }
`;

const Title = styled.div`
    height: 10px;
    font-size: 0.5em;
    font-weight: bold;
    line-height: 1.25;
    letter-spacing: 2px;
    color: #5d656c;
    text-transform: uppercase;
`;

const ActionName = styled.div`
    height: 20px;
    font-size: 0.875em;
    font-weight: 600;
    line-height: 1.43;
    margin-top: 3px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: calc(100% - 42px);

    @media (max-width: 30em) {
        font-size: 14px;
        height: auto;
    }
`;

const TextContainer = styled.div`
    width: 100%;
`;

const LogoTimer = styled.div`
    display: block;
    width: 31px;
    height: 31px;
    display: flex;
    align-items: center;
    position: absolute;
    right: 11px;
    top: 12px;
`;

const AnimationTimer = styled.div`
    visibility: visible;
    transition: visibility 0.3s;

    &.hide {
        visibility: hidden;
    }
`;

const CountdownTimerValue = styled.div`
    visibility: hidden;
    transition: visibility 0.3s;
    position: absolute;
    right: 23px;
    top: 18px;
    color: #fff;
    font-size: 14px;

    img {
        width: 6px;
        margin-top: 4px;
    }

    &.visible {
        visibility: visible !important;
    }
`;

const CountdownTimer = styled.div`
    visibility: hidden;
    transition: visibility 0.3s;
    width: 100%;
    height: 100%;
    position: absolute;
    right: 0;
    top: 0px;

    &.visible {
        visibility: visible !important;
    }
`;

const CountdownWrapper = styled.div`
    width: 54px;
    height: 54px;
    position: absolute;
    right: 24px;
    top: 0px;

    > div {
        transition: visibility 0.3s;
    }

    @media (min-width: 30em) {
        &:hover {
            > div {
                visibility: hidden;

                &:last-child {
                    visibility: visible !important;
                }
            }
        }
    }
`;

const FinalTimer = styled.div`
    visibility: hidden;
    border-radius: 100%;
    width: 32px;
    height: 32px;
    text-align: center;
    line-height: 32px;
    position: absolute;
    right: 11px;
    top: 11px;
    background: #ff245e;
`;

const RightArrow = styled.img`
    width: 7px;
    height: auto;
`;

class NextAction extends React.Component {
    componentDidMount() {
        this.browser = detect();
        const second = 1000;
        const isEdge = this.browser.name === 'edge';
        this.timeRemaining = (this.props.countdownTimer + 2.25) * second;
        this.lottieDelay = isEdge ? 0 : 1.5 * second;
        this.numberDisplayDelay = isEdge ? 0 : 2.25 * second;
        this.paused = false;

        if (this.props.nextAction != null) {
            this.timerStart = new Date();
            this.timeInterval = setInterval(() => this.timeProgression(100), 100);
        }

        if (this.animationTimer && this.countdownTimer && this.countdownTimerValue) {
            this.loadCountdownAnimations();
        }
    }

    loadCountdownAnimations = () => {
        this.lottieTimeout = setTimeout(() => {
            this.lottieCallback();
        }, this.lottieDelay);
        this.numberDisplayInterval = setInterval(() => {
            this.intervalCallback();
        }, 1000);

        this.toggleNumberDisplayInterval = setTimeout(() => {
            if (this.animationTimer && this.countdownTimer && this.countdownTimerValue) {
                this.animationTimer.classList.add('hide');
                this.countdownTimer.classList.add('visible');
                this.countdownTimerValue.classList.add('visible');
            }
        }, this.numberDisplayDelay);
    };

    lottieCallback() {
        if (lottie && this.countdownTimer) {
            lottie.loadAnimation({
                container: this.countdownTimer,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: countdownAnimation
            });
        }
    }

    intervalCallback() {
        if (this.paused) return;
        if (this.timeRemaining <= 0) {
            this.nextAction();
            return;
        }
        if (this.countdownTimerValue) {
            const secondsRemaining = Math.floor(this.timeRemaining / 1000);
            this.countdownTimerValue.innerHTML =
                secondsRemaining % 2 !== 0
                    ? '<img src="' + rightArrow + '">'
                    : secondsRemaining;
        }
    }

    timeProgression(interval) {
        if (!this.paused) this.timeRemaining -= interval;
    }

    pauseTimers(shouldIncreaseTimer) {
        this.paused = true;
        const additionalTime = shouldIncreaseTimer ? 2000 : 0;
        this.timeRemaining > 8000
            ? (this.timeRemaining = 8000)
            : (this.timeRemaining += additionalTime);
        clearTimeout(this.lottieTimeout);
        this.lottieDelay -= new Date() - this.timerStart;
    }

    resumeTimers(shouldIncreaseTimer) {
        this.paused = false;
        clearTimeout(this.lottieTimeout);
        this.lottieTimeout = setTimeout(() => {
            this.lottieCallback();
        }, this.lottieDelay);
    }

    nextAction() {
        this.props.onGoToNextCard(
            this.props.nextAction.identifier,
            this.props.nextCard.type,
            this.props.nextAction.type,
            this.props.skipFrontCardOnNextAction,
            false
        );
    }

    componentWillUnmount() {
        if (this.lottieTimeout != null) {
            clearTimeout(this.lottieTimeout);
            clearInterval(this.numberDisplayInterval);
            clearInterval(this.timeInterval);
        }
    }

    componentDidUpdate(prevProps) {
        const componentTurnedOff = propName =>
            prevProps[propName] === true && this.props[propName] === false;
        const componentTurnedOn = propName =>
            prevProps[propName] === false && this.props[propName] === true;
        if (
            componentTurnedOff('isOverlayVisible') ||
            componentTurnedOff('isShareDrawerVisible') ||
            componentTurnedOff('isOverlayPublisherVisible') ||
            componentTurnedOff('isOverlayPartnerVisible') ||
            componentTurnedOff('sliderIsInUse')
        ) {
            this.resumeTimers(prevProps.sliderIsInUse);
        } else if (
            componentTurnedOn('isOverlayVisible') ||
            componentTurnedOn('isShareDrawerVisible') ||
            componentTurnedOn('isOverlayPublisherVisible') ||
            componentTurnedOn('isOverlayPartnerVisible') ||
            componentTurnedOn('sliderIsInUse')
        ) {
            this.pauseTimers(!prevProps.sliderIsInUse);
        }
    }

    render() {
        return this.props.nextAction ? (
            <Container
                isVisual={this.props.isVisual}
                hideFooter={this.props.hideFooter}
                data-container="true"
                hasNextAction={this.props.nextAction !== null}
                onClick={() => {
                    if (this.lottieTimeout != null) {
                        clearTimeout(this.lottieTimeout);
                        clearInterval(this.numberDisplayInterval);
                        clearInterval(this.timeInterval);
                    }
                    let dataContainer = document.querySelectorAll('[data-container]')[0];
                    dataContainer.classList.add('hide');
                    this.nextAction();
                }}
            >
                <Body>
                    <TextContainer>
                        <Title>{copyFor('nextAction', 'title')}</Title>
                        <ActionName
                            dangerouslySetInnerHTML={{
                                __html: this.props.nextCardName
                            }}
                        />
                    </TextContainer>
                    <CountdownWrapper>
                        <AnimationTimer
                            innerRef={i => {
                                this.animationTimer = i;
                            }}
                        >
                            <Donut />
                            <LogoTimer>
                                <Logo size="15" />
                            </LogoTimer>
                        </AnimationTimer>
                        <CountdownTimer
                            innerRef={i => {
                                this.countdownTimer = i;
                            }}
                        />
                        <CountdownTimerValue
                            innerRef={i => {
                                this.countdownTimerValue = i;
                            }}
                        />
                        {!this.props.isMobileResolution && (
                            <FinalTimer>
                                <RightArrow src={rightArrow} />
                            </FinalTimer>
                        )}
                    </CountdownWrapper>
                </Body>
            </Container>
        ) : (
            <div />
        );
    }
}

NextAction.propTypes = {
    isVisual: PropTypes.bool,
    countdownTimer: PropTypes.number.isRequired,
    hideFooter: PropTypes.bool,
    isMobileResolution: PropTypes.bool.isRequired,
    nextAction: PropTypes.object,
    nextCard: PropTypes.object,
    nextCardName: PropTypes.string.isRequired,
    onGoToNextCard: PropTypes.func.isRequired,
    skipFrontCardOnNextAction: PropTypes.bool,
    sliderIsInUse: PropTypes.bool
};

export default connect(
    state => {
        const reps = state.localRep.localRepData
            ? relevantRepresentatives(
                  state.widget.actions[state.widget.activeAction.order]
                      ? state.widget.actions[state.widget.activeAction.order]
                            .representativeSearch
                      : null,
                  state.localRep.localRepData.representative
              ).slice(0, 3)
            : [];

        const formatPlaceholders = (str, location) => {
            const i = str.indexOf(Placeholders.LocalRepresentative);
            if (reps.length > 0) {
                const repString = joinEnglish(reps.map(r => r.shortName));
                return str
                    .replace(new RegExp(Placeholders.LocalRepresentative, 'g'), repString)
                    .replace(new RegExp(Placeholders.LocalePlaceholder, 'g'), location);
            } else {
                return str
                    .replace(
                        new RegExp(Placeholders.LocalRepresentative, 'g'),
                        i === 0 || str.charAt(i - 2) === '.'
                            ? copyFor(
                                  'nextAction',
                                  'yourReps',
                                  undefined,
                                  capitalization.first
                              )
                            : copyFor('nextAction', 'yourReps')
                    )
                    .replace(new RegExp(Placeholders.LocalePlaceholder, 'g'), location);
            }
        };
        const activeAction = state.widget.confirmationInfo.oldAction;
        const nextAction = state.widget.confirmationInfo.newAction;

        const nextCard = nextAction ? state.widget.cards[nextAction.order - 1] : null;
        const defaultNextCardName = copyFor('nextAction', 'defaultText');

        return {
            nextAction,
            nextCard,
            skipFrontCardOnNextAction:
                nextAction != null ? nextAction.skipFrontCard : null,
            isLocalRepAction:
                nextAction != null ? isLocalRepresentativeAction(nextAction.type) : false,
            nextCardName: formatPlaceholders(
                nextAction
                    ? (nextCard || {}).heading || nextAction.name || defaultNextCardName
                    : defaultNextCardName,
                (state.widget.endUserLocation || {}).regionName || 'your area'
            ),
            isOneActionCard: nextAction
                ? state.widget.cards[nextAction.order - 1].type === CardTypes.oneAction
                : false,
            countdownTimer: activeAction.cardCountdownTimer,
            isMobileResolution: state.widget.isMobileResolution,
            isOverlayVisible: state.widget.isOverlayVisible,
            sliderIsInUse: state.sentimentPoll.isSubmitting,
            isShareDrawerVisible: state.widget.shareDrawerVisibility,
            isOverlayPublisherVisible: state.widget.isOverlayPublisherVisible,
            isOverlayPartnerVisible: state.widget.isOverlayPartnerVisible
        };
    },
    { onGoToNextCard }
)(NextAction);
